import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash-es';
import { AText, Box, Button } from '@alf/uikit';
import { addUserToSegment } from '@namespace/ab-test';
import { useABTestStore } from '@namespace/ab-test/src/store';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { useOtherSettings } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import { useGetPlatformForFeed } from '@namespace/games';
import {
  getLocalStorageValue,
  LOCAL_STORAGE_KEYS,
  setLocalStorageValue
} from '@namespace/helpers';
import { LocalizedLink, useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import { UserContext } from '@namespace/user';

import styles from './index.module.css';

const OnboardingPopupBare = ({ popupToShow, baseImageUrl }) => {
  const [isDisplayPopup, setIsDisplayPopup] = useState(false);
  const { t, f, language } = useI18n();
  const DATE_FORMATS_TYPES = f.getFormats();
  const { isMobile } = useDeviceStore();
  const { abSegments: userSegments = [], isSegmentsLoaded } = useABTestStore([
    'abSegments',
    'isSegmentsLoaded'
  ]);
  const platform = useGetPlatformForFeed();
  const [user] = useContext(UserContext);
  const { id: userId } = user;

  const {
    segmentName,
    title,
    desktopImgName,
    mobileImgName,
    infoText,
    link,
    buttonTitle
  } = popupToShow;

  const currentImageUrl = useMemo(() => {
    if (isMobile) {
      return `${baseImageUrl}${language}/${mobileImgName}`;
    }

    return `${baseImageUrl}${language}/${desktopImgName}`;
  }, [baseImageUrl, desktopImgName, isMobile, language, mobileImgName]);

  const isDisplayAfterLogin = getLocalStorageValue(
    LOCAL_STORAGE_KEYS.IS_DISPLAY_ONBOARDING_POPUP,
    false
  );

  const onClose = (analyticAction) => {
    setIsDisplayPopup(false);
    logAction(analyticAction, {
      playerId: userId,
      dateTime: f
        .getDateTime()
        .toFormat(DATE_FORMATS_TYPES.DEPOSIT_HISTORY_DATE_TIME),
      segment: segmentName,
      platform,
      popupName: segmentName
    });
  };

  useEffect(() => {
    (async () => {
      if (
        segmentName &&
        isSegmentsLoaded &&
        isDisplayPopup &&
        userSegments.includes(segmentName)
      ) {
        await addUserToSegment(`${segmentName}_seen`);
        setLocalStorageValue(
          LOCAL_STORAGE_KEYS.IS_DISPLAY_ONBOARDING_POPUP,
          false
        );
        logAction(LOG_ACTIONS.ONBOARDING_POPUP_VIEW, {
          playerId: userId,
          dateTime: f
            .getDateTime()
            .toFormat(DATE_FORMATS_TYPES.DEPOSIT_HISTORY_DATE_TIME),
          segment: segmentName,
          platform,
          popupName: segmentName
        });
      }
    })();
  }, [segmentName, isSegmentsLoaded, userSegments, isDisplayPopup]);

  useEffect(() => {
    if (isDisplayAfterLogin && isSegmentsLoaded && !isEmpty(popupToShow)) {
      setIsDisplayPopup(true);
    }
  }, [isDisplayAfterLogin, isSegmentsLoaded, popupToShow]);

  return (
    isDisplayPopup && (
      <Box justify="center" className={styles.container}>
        <div className={styles.overlay} />
        <Box direction="column" className={styles.content}>
          <Box className={styles.header} align="center" justify="between">
            <AText breed="B" size="l" color="po_txt_color_2">
              {t('web.onboarding.popup.poc.information')}
            </AText>
            <Icon
              name="icons/general/action/close"
              color="txt_color_4"
              size="l"
              className={styles.closeIcon}
              onClick={() => onClose(LOG_ACTIONS.ONBOARDING_POPUP_CLOSE_CLICK)}
            />
          </Box>
          <Box direction="column" className={styles.popupContent}>
            <AText
              breed="B"
              size="xl"
              color="txt_color_3"
              className={styles.title}
            >
              {t(title)}
            </AText>
            <Box className={styles.imageContainer}>
              <img
                src={currentImageUrl}
                alt="onboarding"
                className={styles.image}
              />
            </Box>
            <AText
              breed="R"
              size="m"
              color="txt_color_3"
              className={styles.info}
            >
              {t(infoText)}
            </AText>
            <Box className={styles.buttonContainer}>
              <Button
                component={LocalizedLink}
                to={link}
                onClick={() =>
                  onClose(LOG_ACTIONS.ONBOARDING_POPUP_BUTTON_CLICK)
                }
                actionType="color1"
                size="l"
                fullWidth={true}
              >
                {t(buttonTitle)}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    )
  );
};

export const OnboardingPopup = () => {
  const { onboardingPopups = {} } = useOtherSettings();
  const { showToTimestamp, list = [], baseImageUrl } = onboardingPopups;
  const { f } = useI18n();
  const { abSegments: userSegments = [], isSegmentsLoaded } = useABTestStore([
    'abSegments',
    'isSegmentsLoaded'
  ]);

  const [popupToShow = {}] = list.filter(
    ({ segmentName }) =>
      userSegments.length &&
      !userSegments.includes(`${segmentName}_seen`) &&
      userSegments.includes(segmentName)
  );

  const isOnboardingPopupAvailable = showToTimestamp * 1000 > f.getDateTime();

  useEffect(() => {
    if (
      (isSegmentsLoaded && isEmpty(popupToShow)) ||
      !isOnboardingPopupAvailable
    ) {
      setLocalStorageValue(
        LOCAL_STORAGE_KEYS.IS_DISPLAY_ONBOARDING_POPUP,
        false
      );
    }
  }, [isSegmentsLoaded]);

  if (!isOnboardingPopupAvailable) {
    return null;
  }

  return (
    <OnboardingPopupBare
      popupToShow={popupToShow}
      baseImageUrl={baseImageUrl}
    />
  );
};
